import { Button } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useInterval } from '@app/domain/domain-events/domain-events.hooks'
import { useListPrograms } from '@app/domain/program/program.hooks'
import type { AppState } from '@app/state'
import { actions as programActions } from '@app/state/program'

import { CreateProgramModal } from './programs/create_program_modal'
import { ProgramListComponent } from './programs/program_list_component'

const INITIAL_PAGE_NUMBER = 1
const INITIAL_RECORDS_PER_PAGE = 50
const RELOAD_INTERVAL_IN_MS = 5000

export const ProgramConfigurationScreen = function () {
  const dispatch = useDispatch()

  const [pageNumber, setPageNumber] = useState<number>(INITIAL_PAGE_NUMBER)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(
    INITIAL_RECORDS_PER_PAGE,
  )

  const [name, setName] = useState<string>('')

  const { programs, totalNumberOfRecords, getPrograms } = useListPrograms()

  const isCreateModalOpen = useSelector<AppState, boolean>(
    (state) => state.program.createProgramModalIsOpen,
  )

  const createProgramErrorMessage = useSelector<AppState, string>(
    (state) => state.program.createProgramErrorMessage,
  )

  const handleCreateProgram = useCallback(() => {
    dispatch(programActions.createProgramModalOpened())
  }, [dispatch])

  const onCreateProgramCancelled = useCallback(() => {
    setName('')
    dispatch(programActions.createProgramModalClosed())
  }, [dispatch])

  const onPerPageSelect = useCallback((recordsPerPage) => {
    setPageNumber(1)
    setRecordsPerPage(recordsPerPage)
  }, [])

  const onSetPage = useCallback((updatedPageNumber) => {
    setPageNumber(updatedPageNumber)
  }, [])

  const onCreateProgram = useCallback(() => {
    dispatch(programActions.createProgram(name))
    setName('')
  }, [dispatch, name])

  const refreshAll = useCallback(() => {
    getPrograms(pageNumber, recordsPerPage)
  }, [getPrograms, pageNumber, recordsPerPage])

  useEffect(() => {
    refreshAll()
  }, [refreshAll])

  useInterval(refreshAll, RELOAD_INTERVAL_IN_MS)

  return (
    <React.Fragment>
      <CreateProgramModal
        isModalOpen={isCreateModalOpen}
        name={name}
        errorMessage={createProgramErrorMessage}
        onCreateProgram={onCreateProgram}
        onCancel={onCreateProgramCancelled}
        onNameChange={setName}
      />
      <Button variant="primary" onClick={handleCreateProgram}>
        Create New Program
      </Button>
      <ProgramListComponent
        programs={programs}
        pagination={{
          pageNumber,
          totalNumberOfRecords,
          recordsPerPage,
          onSetPage,
          onPerPageSelect,
        }}
        getProgramLink={(programId) => `/clinic/program/${programId}`}
      />
    </React.Fragment>
  )
}
